import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSupabase } from '../supabaseAuth';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const { signup, loginWithSocial, loading, error, isAuthenticated } = useSupabase();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validate required fields
    if (!email || !password || !confirmPassword || !firstName || !lastName) {
      setFormError('Please fill in all required fields');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }

    try {
      const userMetadata = {
        first_name: firstName,
        last_name: lastName
      };

      await signup(email, password, userMetadata);
      navigate('/login', {
        state: { message: 'Account created successfully! Please check your email to verify your account.' }
      });
    } catch (error) {
      console.error('Signup error:', error);
      setFormError(error.message || 'Failed to create account. Please try again.');
    }
  };

  const handleSocialLogin = async (provider) => {
    try {
      await loginWithSocial(provider);
    } catch (error) {
      console.error(`${provider} login error:`, error);
      setFormError(error.message || `Failed to login with ${provider}`);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 pt-20 md:pt-24">
      <main className="w-full flex flex-col gap-2.5 items-center p-5 md:p-12 md:pt-0">
        {/* Page Header */}
        <h1 className="text-3xl font-bold text-black mb-4">Sign Up</h1>

        {/* Signup Container */}
      <div className="bg-white rounded-2xl shadow-md w-full max-w-2xl flex flex-col items-center gap-3 p-8 md:p-10">
          <div className="flex flex-col w-full max-w-lg gap-3 flex-1">

            {/* Welcome Section */}
            <div className="flex flex-col items-center text-center w-full mb-2">
              <h2 className="text-[26px] font-semibold text-black mb-1">Welcome to Auric!</h2>
              <p className="text-gray-600 text-base leading-relaxed">
                Tell us about yourself to get started with your precious metals investment journey.
              </p>
            </div>

            {/* Error Messages */}
            {(error || formError) && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {formError || error}
              </div>
            )}

            {/* Signup Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 flex-1">
              <div className="flex flex-col gap-2.5">
                {/* Name Field */}
                <div className="flex flex-col">
                  <label className="font-medium mb-1">Name</label>
                  <div className="flex gap-2.5">
                    <input
                      type="text"
                      className="flex-1 border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={loading}
                      required
                    />
                    <input
                      type="text"
                      className="flex-1 border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={loading}
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="flex flex-col">
                  <label className="font-medium mb-1">Email</label>
                  <input
                    type="email"
                    className="border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                    placeholder="example@gmail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>

                {/* Password Fields */}
                <div className="flex gap-2.5">
                  <div className="flex flex-col flex-1">
                    <label className="font-medium mb-1">Password</label>
                    <div className="relative flex items-center">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="w-full border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                        placeholder="••••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 bg-transparent border-none text-gray-500 cursor-pointer"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col flex-1">
                    <label className="font-medium mb-1">Confirm Password</label>
                    <div className="relative flex items-center">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="w-full border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                        placeholder="••••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading}
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 bg-transparent border-none text-gray-500 cursor-pointer"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        <i className={`bi bi-eye${showConfirmPassword ? '-slash' : ''}`}></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col items-center w-full gap-3 mt-2">
                <button
                  type="submit"
                  className="w-full h-12 rounded-full bg-black text-white font-bold text-base border-none hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>

                {/* Divider */}
                <div className="flex items-center text-center w-full my-1">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="px-4 text-gray-700 text-base">Or</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Google Sign Up */}
                <button
                  type="button"
                  onClick={() => handleSocialLogin('google')}
                  className="flex items-center justify-center bg-white border-2 border-black rounded-full h-12 w-full text-black font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  <svg className="w-[18px] h-[18px] mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue With Google
                </button>

                {/* Sign In Link */}
                <div className="text-center text-gray-700">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-black no-underline font-bold hover:underline"
                  >
                    Sign In
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Signup;
